import axios from 'axios';
import {settings} from '@/plugins/settings';
import {IRouteCalculationData, IRouteCalculationStop, IRouteParsedLeg, IRouteStartingLocation} from '@/model/Route/routeInterfaces';

let googleMapsLoader: Promise<any> | null = null;

function fetchRouteCalculationData(
  orderIds: number[],
  routeStops: IRouteCalculationStop[],
  driverId: number,
  startingLocation: IRouteStartingLocation | null
): Promise<IRouteCalculationData> {
  const payload: any = {
    driver_id: driverId,
    order_ids: orderIds,
    route_stops: routeStops,
  };

  if (startingLocation) {
    payload.starting_location = {
      lat:     startingLocation.lat,
      lng:     startingLocation.lng,
      address: startingLocation.address,
    };
  }

  return axios.post(settings.endpoint + 'routes/waypoints', payload)
    .then((response: any) => response.data.data as IRouteCalculationData);
}

export function loadGoogleMaps(apiKey: string): Promise<any> {
  const globalWindow = window as any;

  if (globalWindow.google && globalWindow.google.maps) {
    return Promise.resolve(globalWindow.google);
  }

  if (googleMapsLoader) {
    return googleMapsLoader;
  }

  googleMapsLoader = new Promise((resolve, reject) => {
    const existingScript = document.getElementById('google-maps-sdk') as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(globalWindow.google));
      existingScript.addEventListener('error', () => {
        googleMapsLoader = null;
        reject(new Error('Unable to load Google Maps.'));
      });
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-sdk';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.onload = () => resolve(globalWindow.google);
    script.onerror = () => {
      googleMapsLoader = null;
      reject(new Error('Unable to load Google Maps.'));
    };

    document.head.appendChild(script);
  });

  return googleMapsLoader;
}

function requestDirections(
  googleMaps: any,
  routeData: IRouteCalculationData
): Promise<any> {
  const directionsService = new googleMaps.maps.DirectionsService();

  return new Promise((resolve, reject) => {
    directionsService.route(
      {
        origin: routeData.origin,
        destination: routeData.destination,
        waypoints: routeData.waypoints,
        optimizeWaypoints: false,
        travelMode: googleMaps.maps.TravelMode.DRIVING,
      },
      (response: any, status: string) => {
        if (status === googleMaps.maps.DirectionsStatus.OK && response) {
          resolve(response);
          return;
        }

        reject(new Error(`ROUTE_DIRECTIONS_FAILED:${status}`));
      }
    );
  });
}

function parseDirectionsResponse(response: any, routeStops: IRouteCalculationStop[]): IRouteParsedLeg[] {
  const route = response && response.routes ? response.routes[0] : null;
  const legs = route && route.legs ? route.legs : [];

  return legs.map((leg: any, index: number) => {
    const stop = routeStops[index] || null;

    return {
      start_address: leg.start_address || '',
      end_address: leg.end_address || '',
      distance: leg.distance ? leg.distance.value : 0,
      duration: leg.duration ? leg.duration.value : 0,
      endpoint_lat: Number(leg.end_location.lat().toFixed(7)),
      endpoint_lng: Number(leg.end_location.lng().toFixed(7)),
      extra_stop_name: stop && stop.type === 'extra' && stop.name ? stop.name : null,
      extra_stop_address: stop && stop.type === 'extra' && stop.address ? stop.address : null,
    };
  });
}

export async function buildParsedRouteLegs(
  orderIds: number[],
  routeStops: IRouteCalculationStop[],
  driverId: number,
  startingLocation: IRouteStartingLocation | null
): Promise<IRouteParsedLeg[]> {
  const routeData = await fetchRouteCalculationData(orderIds, routeStops, driverId, startingLocation);

  if (!routeData.google_maps_api_key) {
    throw new Error('Google Maps API key is not configured.');
  }

  const googleMaps = await loadGoogleMaps(routeData.google_maps_api_key);
  const directionsResponse = await requestDirections(googleMaps, routeData);

  return parseDirectionsResponse(directionsResponse, routeStops);
}
